import { API_URL, handleUnauthorized } from './api';

/** Error thrown for a non-2xx API response. */
export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/**
 * JSON fetch against the API. Sends the httpOnly session cookie and routes a
 * 401 through the shared unauthorized handler.
 */
export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  if (response.status === 401) {
    handleUnauthorized();
    throw new ApiError('Unauthorized', 401);
  }

  if (!response.ok) {
    const message =
      (await response.text().catch(() => '')) || `Request failed (${response.status})`;
    throw new ApiError(message, response.status);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * Unwraps an Eden Treaty response, returning the typed body or throwing an
 * {@link ApiError}. Session expiry (401/403) is already handled by the SDK's
 * `onResponse` hook.
 *
 * The treaty body is cast to `T` because Eden types serialized `Date` columns as
 * `Date` while the wire format is ISO strings; the CMS DTOs describe the wire.
 */
export function unwrap<T>(response: { data: unknown; error: unknown; status: number }): T {
  if (response.error) {
    const value = response.error as { message?: unknown; value?: unknown };
    const raw =
      typeof value?.value === 'string'
        ? value.value
        : typeof value?.message === 'string'
          ? value.message
          : `Request failed (${response.status})`;

    let message = raw;
    try {
      const parsed = JSON.parse(raw) as { message?: unknown };
      if (typeof parsed?.message === 'string') message = parsed.message;
    } catch {
      // not JSON, use as-is
    }

    throw new ApiError(message, response.status);
  }

  return response.data as T;
}
